import React from 'react';
import { Box, Container, Typography, Divider } from '@mui/material';

export default function CompliancePolicy() {
  return (
    <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="md">
        {/* Header */}
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, color: '#111827' }}>
          Compliance Policy
        </Typography>
        <Typography variant="body2" sx={{ color: '#6b7280', mb: 4 }}>
          Last Updated: October 2025
        </Typography>

        <Divider sx={{ mb: 4 }} />

        {/* Introduction */}
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8 }}>
          Sniffr.ai ("we", "our", or "us") is committed to maintaining the highest standards of data protection 
          and regulatory compliance. This Compliance Policy outlines our approach to data handling, privacy 
          regulations, and our commitment to protecting your personal information in accordance with applicable 
          laws and standards.
        </Typography>

        {/* Compliance Standards */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          1. Compliance Standards
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
          We adhere to the following regulatory frameworks and standards:
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8, pl: 3 }}>
          • <strong>GDPR (General Data Protection Regulation):</strong> European Union data protection requirements<br />
          • <strong>CCPA (California Consumer Privacy Act):</strong> California privacy rights and obligations<br />
          • <strong>SOC 2:</strong> Security, availability, and confidentiality controls<br />
          • <strong>ISO 27001:</strong> Information security management standards<br />
          • Other applicable regional and industry-specific regulations
        </Typography>

        {/* Data Regions and Storage */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          2. Data Regions and Storage
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8 }}>
          Your data is stored and processed in secure, geographically distributed data centers. We maintain 
          strict controls over data residency and ensure that data processing complies with regional data 
          protection laws. Data may be stored in regions including but not limited to the United States, 
          European Union, and other jurisdictions as required for service delivery.
        </Typography>

        {/* Lawful Basis for Processing */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          3. Lawful Basis for Processing
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
          We process personal data based on the following lawful grounds:
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8, pl: 3 }}>
          • <strong>Consent:</strong> When you have provided explicit consent for specific processing activities<br />
          • <strong>Contract Performance:</strong> To fulfill our contractual obligations and provide services<br />
          • <strong>Legal Obligation:</strong> To comply with applicable laws and regulatory requirements<br />
          • <strong>Legitimate Interests:</strong> For business operations, security, and service improvement (where balanced with your rights)
        </Typography>

        {/* Consent Mechanisms */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          4. Consent Mechanisms
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8 }}>
          We obtain your consent through clear, informed, and explicit mechanisms. You have the right to withdraw 
          consent at any time through your account settings or by contacting us directly. Withdrawal of consent 
          will not affect the lawfulness of processing based on consent before its withdrawal.
        </Typography>

        {/* Purposes of Processing */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          5. Purposes of Processing
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
          We process personal data for the following purposes:
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8, pl: 3 }}>
          • Providing and maintaining our AI-powered code analysis services<br />
          • Authenticating users and managing accounts<br />
          • Processing payments and managing subscriptions<br />
          • Communicating service updates, security alerts, and support responses<br />
          • Improving our services through analytics and user feedback<br />
          • Ensuring security, preventing fraud, and protecting against unauthorized access<br />
          • Complying with legal obligations and responding to lawful requests
        </Typography>

        {/* Categories of Personal Information */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          6. Categories of Personal Information (PII)
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
          We collect and process the following categories of personal information:
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8, pl: 3 }}>
          • <strong>Identity Data:</strong> Name, email address, username, job title<br />
          • <strong>Contact Data:</strong> Email address, company name, phone number (if provided)<br />
          • <strong>Account Data:</strong> Login credentials, subscription information, payment details<br />
          • <strong>Usage Data:</strong> Service usage patterns, feature interactions, analytics data<br />
          • <strong>Technical Data:</strong> IP address, browser type, device information, cookies<br />
          • <strong>Content Data:</strong> Code repositories, pull requests, and analysis results (processed securely)
        </Typography>

        {/* Data Retention */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          7. Data Retention
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8 }}>
          We retain personal data only for as long as necessary to fulfill the purposes outlined in this policy, 
          comply with legal obligations, resolve disputes, and enforce our agreements. Retention periods vary 
          based on data type and purpose. Account data is retained while your account is active and for a 
          reasonable period thereafter. You may request deletion of your data at any time, subject to legal 
          retention requirements.
        </Typography>

        {/* Data Anonymization */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          8. Data Anonymization and Aggregation
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8 }}>
          We may anonymize or aggregate personal data for analytical, research, or service improvement purposes. 
          Anonymized data cannot be linked back to individual users and is not subject to data protection 
          regulations. This allows us to improve our services while protecting your privacy.
        </Typography>

        {/* Security Measures */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          9. Security Measures
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
          We implement comprehensive security measures to protect your data:
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8, pl: 3 }}>
          • Encryption in transit (TLS/SSL) and at rest<br />
          • Access controls and authentication mechanisms<br />
          • Regular security audits and vulnerability assessments<br />
          • Incident response procedures and breach notification protocols<br />
          • Employee training on data protection and security best practices
        </Typography>

        {/* Third-Party Processors */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          10. Third-Party Processors
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8 }}>
          We may engage trusted third-party service providers to assist with data processing, hosting, payment 
          processing, and analytics. All third-party processors are contractually obligated to maintain the same 
          level of data protection and security standards. We conduct due diligence on all processors and ensure 
          they comply with applicable data protection regulations.
        </Typography>

        {/* Data Subject Rights (DSR) */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          11. Data Subject Rights (DSR)
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
          You have the following rights regarding your personal data:
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8, pl: 3 }}>
          • <strong>Right to Access:</strong> Request a copy of your personal data<br />
          • <strong>Right to Rectification:</strong> Correct inaccurate or incomplete data<br />
          • <strong>Right to Erasure:</strong> Request deletion of your personal data<br />
          • <strong>Right to Restrict Processing:</strong> Limit how we use your data<br />
          • <strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format<br />
          • <strong>Right to Object:</strong> Object to processing based on legitimate interests<br />
          • <strong>Right to Withdraw Consent:</strong> Withdraw previously given consent
        </Typography>

        {/* DSR Contact */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          12. Exercising Your Rights
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
          To exercise any of your data subject rights, please contact us at:
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8 }}>
          <strong>Email:</strong> support@sniffr.ai<br />
          <strong>Subject Line:</strong> "Data Subject Rights Request"<br />
          <strong>Website:</strong> https://sniffr.ai
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8 }}>
          We will respond to your request within 30 days (or as required by applicable law). Please include 
          sufficient information to verify your identity and specify which rights you wish to exercise.
        </Typography>

        {/* Updates to Policy */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          13. Updates to This Policy
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8 }}>
          We may update this Compliance Policy periodically to reflect changes in our practices, services, or 
          applicable regulations. We will notify you of material changes through email or prominent notices on 
          our website. Continued use of our services after such changes constitutes acceptance of the updated policy.
        </Typography>

        {/* Contact */}
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 2, color: '#1f2937' }}>
          14. Contact Us
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 2, lineHeight: 1.8 }}>
          For questions about this Compliance Policy, our data practices, or to report a security concern, please contact:
        </Typography>
        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8 }}>
          <strong>Email:</strong> support@sniffr.ai<br />
          <strong>Website:</strong> https://sniffr.ai
        </Typography>

        <Typography variant="body1" sx={{ color: '#374151', mb: 4, lineHeight: 1.8, fontStyle: 'italic' }}>
          Sniffr.ai is a product developed and managed by QDEX Consulting.
        </Typography>

        <Divider sx={{ my: 4 }} />

        <Typography variant="body2" sx={{ color: '#6b7280', textAlign: 'center' }}>
          © 2025 Sniffr.ai. All rights reserved.
        </Typography>
      </Container>
    </Box>
  );
}
